// Alben der Galerie - reine Logik, ohne React, ohne Firebase.

/**
 * Die Fotos von vor v0.9.15 haben kein Album. Sie verschwinden nicht, sondern
 * sammeln sich unter dieser Kennung.
 *
 * Bewusst KEIN Migrationslauf, der ihnen nachtraeglich ein Album zuweist: Er
 * muesste raten, welches Foto zu welchem Treffen gehoert - und das Datum des
 * Uploads ist nicht das Datum der Aufnahme. Ein virtuelles Album kostet
 * nichts und behauptet nichts Falsches.
 */
export const LEGACY_ALBUM_ID = '__ohne__';
export const LEGACY_ALBUM_TITLE = 'Bisherige Bilder';

/** 'YYYY-MM-DD' aus einem Firestore-Timestamp oder Date - fuer <input type="date">. */
export function toDateInputValue(value) {
  const date = value?.toDate?.() ?? (value instanceof Date ? value : null);
  if (!date || Number.isNaN(date.getTime())) return '';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * '2026-09-27' -> Date (lokal, 12:00 Uhr).
 *
 * Mittags und nicht um Mitternacht: Ein Datum ohne Uhrzeit, das als UTC
 * gelesen wird, rutscht in Deutschland sonst auf den Vortag.
 */
export function fromDateInputValue(value) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(year, month - 1, day, 12, 0, 0);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Anzeigeform: '27. September 2026'. */
export function formatAlbumDate(value) {
  const date = value?.toDate?.() ?? (value instanceof Date ? value : null);
  if (!date || Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' });
}

/**
 * Der Alternativtext eines Galeriefotos.
 *
 * ── Warum es das braucht ─────────────────────────────────────────────
 * Die Bildunterschrift dient in der Galerie ausdruecklich auch als
 * Alternativtext (so steht es schon im Kommentar von `photoAlt` in
 * `eventImages.js`). Nur hat von den 104 Bestandsfotos keines eine, und der
 * bisherige Rueckfall war fuer alle derselbe Satz: In einem Album mit 39
 * Bildern las ein Screenreader 39-mal "Foto der Club Mini-Thrills Crew".
 * Das ist nicht falsch, aber es unterscheidet nichts - man kann sich darin
 * nicht bewegen.
 *
 * Die Zaehlung samt Albumtitel sagt mehr, kostet keine Handarbeit an 104
 * Fotos und ist nie unwahr. Eine echte Bildunterschrift bleibt besser und
 * hat deshalb IMMER Vorrang - wer eine pflegt, bekommt sie unveraendert.
 *
 * Gleiches Muster wie `photoAlt(event, photo, index)`, nur mit dem Album als
 * Bezug statt des Termins.
 *
 * @param {object} photo   das Foto (nur `caption` zaehlt hier)
 * @param {object} album   das Album, oder null/undefined ausserhalb eines
 * @param {number} index   nullbasiert, wie er in der Liste steht
 * @param {number} total   Fotos im Album; 0/undefined laesst "von N" weg
 */
export function galleryPhotoAlt(photo, album, index, total) {
  const caption = typeof photo?.caption === 'string' ? photo.caption.trim() : '';
  if (caption) return caption;

  const position = Number.isInteger(index) && index >= 0 ? `Foto ${index + 1}` : 'Foto';
  // "von 39" nur, wenn die Zahl auch stimmt - sonst behauptet der Text eine
  // Gesamtmenge, die es nicht gibt (z. B. im Teaser, der nur drei zeigt).
  const menge = Number.isInteger(total) && total > 0 ? ` von ${total}` : '';
  const titel = typeof album?.title === 'string' && album.title.trim() ? album.title.trim() : '';

  return titel ? `${position}${menge} — ${titel}` : `${position}${menge}`;
}

/** Die Fotos EINES Albums, in ihrer kuratierten Reihenfolge. */
export function photosOfAlbum(photos, albumId) {
  const wanted = albumId === LEGACY_ALBUM_ID ? null : albumId;
  return (photos ?? []).filter((photo) => (photo?.albumId ?? null) === wanted);
}

/**
 * Alben mit ihren Fotos, Titelbild und Anzahl - fertig fuer die Uebersicht.
 *
 * Gruppiert wird CLIENTSEITIG aus der ohnehin geladenen Fotoliste, nicht per
 * Abfrage je Album: Das waere ein Firestore-Read pro Album, nur um Kacheln zu
 * fuellen. Die Galerie eines Clubs hat Dutzende Fotos, keine Zehntausende.
 *
 * Das Titelbild ist das erste Foto des Albums. Ein eigenes Feld dafuer waere
 * ein zweiter Ort, an dem eine Bildadresse gepflegt werden muesste - und beim
 * Loeschen genau dieses Fotos zeigte die Kachel ins Leere.
 *
 * Leere Alben bleiben in der Liste: Wer eines anlegt und die Fotos erst
 * spaeter hochlaedt, soll es wiederfinden.
 */
export function buildAlbums(albums, photos) {
  const list = (albums ?? []).map((album) => {
    const albumPhotos = photosOfAlbum(photos, album.id);
    return {
      ...album,
      photos: albumPhotos,
      count: albumPhotos.length,
      cover: albumPhotos[0] ?? null,
    };
  });

  const orphans = photosOfAlbum(photos, LEGACY_ALBUM_ID);
  if (orphans.length > 0) {
    list.push({
      id: LEGACY_ALBUM_ID,
      title: LEGACY_ALBUM_TITLE,
      date: null,
      legacy: true,
      photos: orphans,
      count: orphans.length,
      cover: orphans[0],
    });
  }

  return list;
}

/**
 * Neueste zuerst, das Sammelalbum immer ans Ende.
 *
 * Ein Album ohne Datum kann es nur als Sammelalbum geben (die Rules verlangen
 * eines) - es hat keine Stelle in einer Zeitachse und wuerde je nach
 * Sortierrichtung ganz oben oder ganz unten einsortiert.
 */
export function sortAlbums(albums) {
  return [...(albums ?? [])].sort((a, b) => {
    if (a.legacy) return 1;
    if (b.legacy) return -1;
    const aTime = a?.date?.toMillis?.() ?? 0;
    const bTime = b?.date?.toMillis?.() ?? 0;
    return bTime - aTime;
  });
}
